import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Dimensions, Linking, Text } from 'react-native'

import { publishEvent } from '@zet/analytics'
import { useToast } from '@zet/common/components/toast'
import { AgentDetails } from '@zet/customer'
import {
  B2,
  BottomSheet,
  Button,
  H5,
  SH3,
  Stack,
  XStack,
  colors,
} from '@zet/design-system'
import { Checkbox } from '@zet/design-system/checkbox'
import Pressable from '@zet/design-system/pressable'
import Close from '@zet/design-system/svg/Close'
import { i18n } from '@zet/localization'
import { styled } from 'nativewind'
import WebView from 'react-native-webview'

import { useGetNextStateForProductFlow } from '../../../../api/brands/hooks'
import { BottomsheetTypes } from '../../../../api/brands/types'
import { useProductFlowApply } from '../../../../hooks/useProductFlowApply'
import {
  experianConsent,
  privacyPolicyForExperianLink,
  privacyPolicyLink,
  termsAndConditionsForExperianLink,
  termsAndConditionsLink,
} from '../../../../utils/constants'
import { Header } from '../header'
import OtpInput from './otpInput'

const BoldText = styled(Text, 'font-[Graphie-Bold] text-sm leading-5')

const OtpVerification = ({
  agentData,
  handleBottomsheetState,
  data,
}: {
  agentData: AgentDetails
  handleBottomsheetState: (state: BottomsheetTypes, data?: any) => void
  data: any
}) => {
  const [value, setValue] = useState({})
  const [isConsentGiven, setIsConsentGiven] = useState<boolean>(false)
  const [timer, setTimer] = useState<number>(30)
  const [showTnCBottomSheet, setShowTnCBottomSheet] = useState(false)
  const [pdfData, setPdfData] = useState({ link: '', title: '' })
  const timerRef = useRef(0)
  const { mutate, isLoading } = useGetNextStateForProductFlow()
  const toast = useToast()
  const { height } = Dimensions.get('window')
  let isExperianEnabled = data?.actionData?.isExperianEnabled

  const interval = 1000
  let prev = 0
  let diff = 0

  const timerCallback = () => {
    const date = new Date()
    setTimer((i) => {
      const current = date.getSeconds()
      if (diff === 0) {
        diff = 1
        prev = date.getSeconds()
        return 30
      }

      if (prev > current) diff = 60 - prev + current
      else diff = current - prev

      prev = date.getSeconds()

      if (i > 1)
        if (i + diff < 1) {
          clearInterval(timerRef.current)
          return 1
        } else {
          return i - diff
        }
      else {
        clearInterval(timerRef.current)
        return 0
      }
    })
  }

  const timerFunction = () => {
    timerRef.current = setInterval(timerCallback, interval)
  }

  const otpTimer = useCallback(timerFunction, [timerRef])

  function closeBottomSheet() {
    handleBottomsheetState(null)
  }

  const handleButtonDisablity = () => {
    const isValueLengthValid = Object.values(value).join('').length === 4

    if (!isValueLengthValid) {
      return true
    }

    return isExperianEnabled && !isConsentGiven
  }

  const { apply, shareLinkLoading } = useProductFlowApply(
    agentData,
    handleBottomsheetState,
  )
  function resendOtp() {
    publishEvent('click_action', {
      item_name: 'shortflow_otp_resend',
    })
    mutate(
      {
        oneCodeId: agentData?.onecodeId,
        userState: 'SHOWN_PHONE_NUMBER_SCREEN',
        customerPayload: {
          fullName: sessionStorage.getItem('fullName'),
          phoneNumber: sessionStorage.getItem('phoneNumber'),
          pinCode: sessionStorage.getItem('pinCode'),
          customerIntentList: [''],
          ...(agentData?.isDsa
            ? {
                meta: {
                  advisorCode: sessionStorage.getItem('advisorCode'),
                },
              }
            : {}),
        },
      },
      {
        onSuccess(data) {
          isExperianEnabled = Boolean(data?.actionData?.isExperianEnabled)
          if (data?.actionData?.lockedTill) {
            toast?.show({
              intent: 'error',
              heading: i18n.t('toastMessages.error'),
              description: data?.actionData?.reason,
              hideAfter: 3000,
            })
            return data?.actionData
          }
        },
      },
    )
  }
  function handleSubmit() {
    publishEvent('click_action', {
      item_name: 'shortflow_otp',
    })
    mutate(
      {
        customerPayload: {
          customerId: sessionStorage.getItem('customerId') as string,
          otp: Object.values(value).join(''),
          phoneNumber: sessionStorage.getItem('phoneNumber') as string,
        },
        userState: 'SHOWN_OTP_SCREEN',
      },
      {
        onSuccess(data) {
          if (data?.code === 400) {
            toast?.show({
              intent: 'error',
              heading: i18n.t('toastMessages.error'),
              description:
                data?.message || i18n.t('toastMessages.somethingWentWrong'),
              hideAfter: 3000,
            })
          }
          if (data?.actionData?.lockedTill) {
            toast?.show({
              intent: 'error',
              heading: i18n.t('toastMessages.error'),
              description: data?.actionData?.reason,
              hideAfter: 3000,
            })
            return data?.actionData
          }
          if (data?.action === 'SHOW_PAN_COLLECTION_SCREEN_V3') {
            handleBottomsheetState('BASIC_DETAILS')
          } else if (data?.action === 'SHOW_BASIC_DETAILS_SCREEN_V3') {
            handleBottomsheetState('EMPLOYMENT_DETAILS')
          } else if (data?.action === 'SHOW_SECONDARY_PHONE_NUMBER_SCREEN_V3') {
            handleBottomsheetState('SECONDARY_PHONE_NUMBERS', data)
          } else if (data?.action === 'REDIRECTION_URL') {
            closeBottomSheet()
            if (Platform.OS === 'web') {
              window.location.href = data?.actionData?.url
            } else {
              Linking.openURL(data?.actionData?.url)
            }
          } else if (data?.action === 'SHOW_RECOMMENDATION_SCREEN') {
            apply()
          }
        },
        onError(err) {
          toast?.show({
            intent: 'error',
            heading: i18n.t('toastMessages.error'),
            description:
              err?.response?.data?.error?.message ||
              i18n.t('toastMessages.somethingWentWrong'),
            hideAfter: 3000,
          })
        },
      },
    )
  }

  function openTermsAndConditions() {
    setShowTnCBottomSheet(true)
    setPdfData({
      link: isExperianEnabled
        ? termsAndConditionsForExperianLink
        : termsAndConditionsLink,
      title: i18n.t('verifyPhNum.terms'),
    })
  }

  function openExperianTermsAndConditions() {
    setShowTnCBottomSheet(true)
    setPdfData({
      link: experianConsent,
      title: i18n.t('webFlow.otpScreen.t&c'),
    })
  }

  function openPrivacyPolicy() {
    setShowTnCBottomSheet(true)
    setPdfData({
      link: isExperianEnabled
        ? privacyPolicyForExperianLink
        : privacyPolicyLink,
      title: i18n.t('verifyPhNum.privacyPolicy'),
    })
  }
  const onDismiss = () => {
    setShowTnCBottomSheet(false)
  }

  useEffect(() => {
    otpTimer()
    return () => {
      clearInterval(timerRef.current)
    }
  }, [])

  return (
    <>
      <Header
        title={i18n.t('addCusProductjourney.otpVerification')}
        closeBottomSheet={closeBottomSheet}
      />
      {isLoading || shareLinkLoading ? (
        <Stack className="pt-6">
          <ActivityIndicator
            size={'large'}
            color={colors.primary[100]}
            className="items-center justify-center"
          />
        </Stack>
      ) : (
        <Stack className="mb-2 pt-3">
          <OtpInput value={value} setValue={setValue} otpCount={4} />

          <Stack className="items-center">
            {timer === 0 ? (
              <Pressable
                onPress={() => {
                  prev = 0
                  diff = 0
                  timerFunction()
                  resendOtp()
                }}
              >
                <SH3 className="text-grays-60">
                  {i18n.t('webFlow.otpScreen.didNot')}
                  <SH3 className="text-primary-100 font-[Graphie-Bold]">
                    {i18n.t('webFlow.otpScreen.resend')}
                  </SH3>
                </SH3>
              </Pressable>
            ) : (
              <B2 className=" text-grays-60 text-center">
                {`Resend OTP in `}
                <SH3 className="text-primary-100">
                  00:{timer < 10 ? `0${timer}` : timer}
                </SH3>
              </B2>
            )}
          </Stack>
          {isExperianEnabled ? (
            <XStack className="mx-4 mt-2 items-center">
              <Stack className="mr-3">
                <Checkbox
                  accesibilityLabel="consentToTnc"
                  checked={isConsentGiven}
                  onChange={setIsConsentGiven}
                />
              </Stack>
              <>
                <B2>
                  {i18n.t('webFlow.otpScreen.iAgree')}
                  <Pressable
                    onPress={openPrivacyPolicy}
                    className=" ml-1 justify-center"
                    eventName="click_action"
                    eventPayload={{
                      item_name:
                        'shortened_journey_add_customer_privacy_policy',
                    }}
                  >
                    <BoldText>{i18n.t('verifyPhNum.privacyPolicy')} </BoldText>
                  </Pressable>
                  {i18n.t('webFlow.otpScreen.and')}
                  <Pressable
                    onPress={openTermsAndConditions}
                    className=" ml-1 justify-center"
                    eventName="click_action"
                    eventPayload={{
                      item_name: 'shortened_journey_add_customer_terms_of_use',
                    }}
                  >
                    <BoldText>{i18n.t('webFlow.otpScreen.terms')}</BoldText>
                  </Pressable>{' '}
                  {i18n.t('webFlow.otpScreen.zet')}
                  <Pressable
                    onPress={openExperianTermsAndConditions}
                    className=" ml-1 justify-center"
                    eventName="click_action"
                    eventPayload={{
                      item_name:
                        'shortened_journey_add_customer_experian_terms_and_conditions',
                    }}
                  >
                    <BoldText>{i18n.t('webFlow.otpScreen.t&c')}</BoldText>
                  </Pressable>{' '}
                  {i18n.t('webFlow.otpScreen.experian')}
                </B2>
              </>
            </XStack>
          ) : (
            <Stack className="mx-3 mt-3">
              <B2 className="" numberOfLines={2}>
                {i18n.t('webFlow.otpScreen.byClicking')}
                <XStack className="items-center">
                  <Pressable
                    onPress={openTermsAndConditions}
                    className=" ml-1 justify-center"
                  >
                    <BoldText>{i18n.t('verifyPhNum.terms')}</BoldText>
                  </Pressable>
                  <B2> {i18n.t('verifyPhNum.and')} </B2>

                  <Pressable onPress={openPrivacyPolicy}>
                    <BoldText>{i18n.t('verifyPhNum.privacyPolicy')}.</BoldText>
                  </Pressable>
                </XStack>
              </B2>
            </Stack>
          )}
        </Stack>
      )}
      <BottomSheet
        visible={showTnCBottomSheet}
        onDismiss={onDismiss}
        snapPoints={[700, 700]}
      >
        <Stack style={{ height: height }}>
          <XStack className="border-grays-30 items-center justify-between border-b px-3 py-2">
            <H5>{pdfData?.title}</H5>
            <Pressable onPress={closeBottomSheet}>
              <Close />
            </Pressable>
          </XStack>
          <WebView
            source={{ uri: pdfData?.link }}
            androidHardwareAccelerationDisabled
          />
        </Stack>
      </BottomSheet>
      <Stack className="bg-neutral-white100 absolute bottom-0 w-full px-3 py-2">
        <Button
          className="bg-tertiary-200 w-full"
          disabled={handleButtonDisablity()}
          onPress={handleSubmit}
          size={'lg'}
          loading={isLoading || shareLinkLoading}
        >
          {i18n.t('bottomView.continue')}
        </Button>
      </Stack>
    </>
  )
}

export { OtpVerification }
